import React, {
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from "react";
import { auth } from "./firebase";
import {
    onAuthStateChanged,
    updateProfile,
    sendEmailVerification,
    reload,
    getIdTokenResult,
} from "firebase/auth";
import { setUserId } from "firebase/analytics";
import { analytics } from "./firebase";
import { getApiUrl } from "../config/api";

const AuthContext = React.createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState();
    const [claims, setClaims] = useState({});
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // fetchUserRoles and autoInitializeUser call each other; the ref breaks the
    // cycle so neither callback has to be redefined on every render.
    const autoInitializeUserRef = useRef(null);

    const fetchUserRoles = useCallback(async (user) => {
        if (!user) {
            setUserRoles([]);
            return;
        }

        try {
            const token = await user.getIdToken(true); // Force refresh to ensure valid token
            const response = await fetch(`${getApiUrl()}/api/user/roles`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUserRoles(data.roles || []);
            } else {
                // If roles fetch fails, it might be because user is not initialized
                // Try to initialize the user automatically
                console.warn(
                    "Failed to fetch user roles, attempting auto-initialization...",
                );
                await autoInitializeUserRef.current?.(user);
                setUserRoles([]);
            }
        } catch (error) {
            console.error("Error fetching user roles:", error);
            // Attempt auto-initialization on error
            await autoInitializeUserRef.current?.(user);
            setUserRoles([]);
        }
    }, []);

    const autoInitializeUser = useCallback(
        async (user) => {
            if (!user || !user.emailVerified) {
                console.log("Skipping auto-initialization: User not verified");
                return;
            }

            try {
                const token = await user.getIdToken(true); // Force refresh to get a valid token
                const response = await fetch(
                    `${getApiUrl()}/api/initialize-user`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    },
                );

                if (response.ok) {
                    console.log("User auto-initialized successfully");
                    // Force token refresh to get new claims
                    await user.getIdToken(true);
                    // Retry fetching roles
                    await fetchUserRoles(user);
                } else {
                    const data = await response.json();
                    console.error("Auto-initialization failed:", data.error);
                }
            } catch (error) {
                console.error("Error during auto-initialization:", error);
            }
        },
        [fetchUserRoles],
    );

    useEffect(() => {
        autoInitializeUserRef.current = autoInitializeUser;
    }, [autoInitializeUser]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            setIsAuthReady(false); // Reset auth ready state

            if (user) {
                try {
                    // Set user ID for analytics
                    if (analytics) {
                        setUserId(analytics, user.uid);
                    }

                    const idTokenResult = await getIdTokenResult(user, true);
                    setClaims(idTokenResult.claims || {});
                    // Fetch user roles from backend based on their group
                    await fetchUserRoles(user);
                    // Set auth as ready after everything is loaded
                    setIsAuthReady(true);
                } catch (err) {
                    console.error("Failed to load token claims", err);
                    setClaims({});
                    setUserRoles([]);
                    setIsAuthReady(true); // Still set ready even on error
                }
            } else {
                // Clear user ID from analytics when user logs out
                if (analytics) {
                    setUserId(analytics, null);
                }
                setClaims({});
                setUserRoles([]);
                setIsAuthReady(true); // User not logged in, but auth state is ready
            }
            setLoading(false);
        });
        return unsubscribe;
    }, [fetchUserRoles]);

    const getUserGroup = useCallback(() => {
        return claims?.groupId || null;
    }, [claims]);

    const hasRole = useCallback(
        (role) => {
            if (!userRoles || userRoles.length === 0) return false;
            // Accept a single role string or an array of roles.
            // Returns true if the user has AT LEAST ONE of the specified roles.
            const roles = Array.isArray(role) ? role : [role];
            return roles.some((r) =>
                userRoles.some(
                    (userRole) => userRole.toLowerCase() === r.toLowerCase(),
                ),
            );
        },
        [userRoles],
    );

    const refreshClaims = useCallback(async () => {
        if (!auth.currentUser) return null;
        try {
            setIsAuthReady(false); // Reset while refreshing
            const idTokenResult = await getIdTokenResult(
                auth.currentUser,
                true,
            );
            setClaims(idTokenResult.claims || {});
            // Also refresh user roles from backend
            await fetchUserRoles(auth.currentUser);
            setIsAuthReady(true); // Set ready after refresh
            return idTokenResult.claims || {};
        } catch (err) {
            console.error("refreshClaims failed", err);
            setIsAuthReady(true); // Set ready even on error
            return null;
        }
    }, [fetchUserRoles]);

    const getUserProfile = () => {
        if (!currentUser) return null;

        return {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            emailVerified: currentUser.emailVerified,
            groupId: claims?.groupId || null,
        };
    };

    const updateUserProfile = async (updates) => {
        if (!currentUser) throw new Error("No user logged in");

        try {
            // Get the current user from auth to ensure we have the latest user object
            const user = auth.currentUser;
            if (!user) throw new Error("No authenticated user found");

            await updateProfile(user, updates);
            setCurrentUser({ ...user, ...updates });
            return { success: true };
        } catch (error) {
            console.error("Profile update error:", error);
            throw error;
        }
    };

    const sendVerificationEmail = async () => {
        if (!currentUser) throw new Error("No user logged in");

        try {
            await sendEmailVerification(currentUser);
            return { success: true };
        } catch (error) {
            console.error("Email verification error:", error);
            throw error;
        }
    };

    const reloadUser = async () => {
        if (!currentUser) return;

        try {
            await reload(currentUser);
            setCurrentUser({ ...currentUser });
        } catch (error) {
            console.error("User reload error:", error);
        }
    };

    const value = {
        currentUser,
        claims,
        userRoles,
        getUserGroup,
        hasRole,
        refreshClaims,
        loading,
        isAuthReady,
        getUserProfile,
        updateUserProfile,
        sendVerificationEmail,
        reloadUser,
        autoInitializeUser,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}
